<template>
  <section class="section is-paddingless">
    <div v-if="contentLoaded"
         class="grid-container flex-grid"
         :style="{'height':  height}">
      <virtual-scroll-list
        :grid-name="gridName"
        :items-found="count"
        :table-columns="tableColumns"
        :table-data="items"
        :title="footerTranslation"
        :wrapper-is-loading="isLoading"
        :is-mobile="isMobile"
        :is-library="true"
        :hide-footer="true"
        :hide-empty-forms-message="true"
        @tobottom="getMoreResults"
        @useWidgetNavToContent="handleWidgetNav">
        <template #filterAndAdd>
          <library-where-used-virtual-filter-bar
            :grid-name="gridName"
            :filters="virtualFilters"
            :show-page-book-select="showPageBookSelect"
            :is-mobile="isMobile"
            :partFilter="partFilter"
            :has-books="hasBooks"
            :has-product-pages="hasProductPages"
            @updatePartFilter="updatePartFilter"
          />
        </template>
      </virtual-scroll-list>
    </div>
    <article class="has-text-weight-bold padme-left-bottom footer">
      {{ `${itemsTotal} ` + footerTranslation }}
    </article>
  </section>
</template>

<script>
import { computed, toRaw } from 'vue'
import { mapState, mapActions, useStore } from 'vuex'
import { useRouter } from 'vue-router'
import VirtualScrollList from '@/components/common/VirtualScrollList'
import LibraryWhereUsedVirtualFilterBar from '@/components/library/content/whereused/LibraryWhereUsedVirtualFilterBar'
import { Pagination } from '@/interfaces/global'
import { search } from '@/controllers/global'
import { VirtualListFilters } from '@/classes/virtualListObjects'
import RaisedCard from '@/components/common/RaisedCard'
import BSelect from "@/buefy/src/components/select/Select";
import debounce from '@/plugins/debounce'
import { WidgetTypes } from '@/interfaces/global'
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator'

export default {
  name: 'WhereUsed',
  mixins: [debounce],
  setup () {
    const store = useStore()
    const router = useRouter()
    const {
      mapDtoAndNavToWidgetContent,
      entityIdToIdDtoMap,
      clearContentState
    } = useWidgetNavigator()

    const isWidget = computed(() => store.getters['widgets/isWidget'])
    const widgetType = computed(() => store.getters['widgets/widgetType'])
    const isLibraryWidget = computed(() => isWidget.value
      && widgetType.value === WidgetTypes.library)
    const content = computed(() => store.state.content)

    function handleWidgetNav (statefulDto) {
      const dto = toRaw(statefulDto.field)
      if (isLibraryWidget.value) {
        if (dto.entityType === 'media') {
          if (content.value?.type === 'part') {
            store.dispatch('search/setTocQuery', `"${content.value?.partNumber}"`)
          } else if (content.value?.type === 'page') {
            store.dispatch('search/setTocQuery', `"${content.value?.originalName}"`)
          }
          store.dispatch('search/clearBomQuery')
        } else {
          if (content.value?.type === 'part') {
            store.dispatch('search/setBomQuery', `"${content.value?.partNumber}"`)
            if (dto.entityType === 'productpage') {
              store.dispatch('search/setPartCollectionQuery', `"${content.value?.partNumber}"`)
              router.push({ path: `/product-page/${dto.entityId}`, params: { id: dto.entityId } })
            }
          } else if (content.value?.type === 'page') {
            store.dispatch('search/setBomQuery', `"${content.value?.originalName}"`)
          }
        }
        clearContentState()
        mapDtoAndNavToWidgetContent(dto, entityIdToIdDtoMap)
      }
    }

    return { isWidget, widgetType, handleWidgetNav, content }
  },
  components: {
    BSelect,
    RaisedCard,
    VirtualScrollList,
    LibraryWhereUsedVirtualFilterBar
  },
  props: {
    height: {
      type: String,
      default: '100%'
    },
  },
  data () {
    return {
      count: 0,
      isLoading: false,
      items: [],
      offsetNextCursor: 0,
      searchInstance: 0,
      screenWidth: window.innerWidth,
      partFilter: 'ALL',
      hasProductPages: false,
      hasBooks: false
    }
  },
  watch: {
    items: {
      immediate: true,
      handler (val) {
        if (this.hasProductPages || this.hasBooks) return
        this.hasProductPages = val.some(item => {
          return item.entityType.toLowerCase() === 'productpage'
        })
        this.hasBooks = this.items.some(item => {
          return item.mediaType?.toLowerCase() === 'book'
        })
      }
    },
    searchParam (updated, previous) {
      if (JSON.stringify(updated) === JSON.stringify(previous)) return
      this.reset()
    },
    searchFilters (updated, previous) {
      if (JSON.stringify(updated) === JSON.stringify(previous)) return
      this.reset()
    },
    activeColumnAndSort (updated, previous) {
      if (JSON.stringify(updated) === JSON.stringify(previous)) return
      this.reset()
    },
    contentLoaded (updated, previous) {
      if (!previous && updated && !this.isLoading) {
        this.reset()
      }
    },
    isMobile (updated, previous) {
      if (previous != updated) {
        const defaultColumn = this.isMobile ? 'name' : 'identifier'
        this.setGridSort({
          grid: this.gridName,
          activeColumn: defaultColumn,
          sortInvert: false
        })
      }
    },
    partFilter(updated, previous) {
      if (previous !== updated && !this.isLoading) {
        this.reset()
      }
    }
  },
  computed: {
    ...mapState({
      contentLoaded: ({ content }) => content.isLoaded,
      tenantKey: (state) => state.user.tenantKey,
      hidePagesInSearch: (state) => state.user.hidePagesInSearch,
      hideBooksInSearch: (state) => state.user.hideBooksInSearch
    }),
    activeColumnAndSort () {
      return '' + this.sortField + this.sortInvert
    },
    virtualFilters () {
      return new VirtualListFilters(
        {
          tagsMatching: {
            indexed: (i) => typeof i === 'number',
            applicableTypes: this.applicableTagTypes
          },
          hasSearchTerm: true
        }
      )
    },
    applicableTagTypes () {
      switch (this.content.type) {
        case "part":
          return ['MEDIA', 'PAGE', 'PRODUCT_PAGE']
        case "media":
          return ["PRODUCT_PAGE"]
        default:
          return ["MEDIA"]
      }
    },
    searchParam () {
      return this.$store.getters['grids/filterTerm'](this.gridName) ?? ''
    },
    searchFilters () {
      const result = { ...this.$store.getters['grids/searchFilters'](this.gridName) }
      if (result.hasOwnProperty('tag_name')) delete result.tag_name
      if (result.hasOwnProperty('tag_value')) delete result.tag_value
      if (result.hasOwnProperty('sortField')) delete result.sortField
      if (result.hasOwnProperty('filterTerm')) delete result.filterTerm
      return result
    },
    hasSearchFilters () {
      return Object.keys(this.searchFilters).length
    },
    gridName () {
      return 'whereUsedLibraryGrid'
    },
    isMedia () {
      return this.content.type === "media"
    },
    tableColumns () {
      return [
        {
          columnClass: 'column',
          field: 'name',
          translation: this.$t('name'),
          type: 'thumbnailWithText',
          isLink: this.isWidget
            ? (this.widgetType === WidgetTypes.library)
            : true,
          isHidden: !this.isMobile,
          isSortable: true,
          getToRoute: this.getToRouteUrl,
          thumbnailType: this.baseThumbnailType,
          url: `https://static.digabit.com/${this.baseThumbnailType === 'page' ? 'page' : 'book'}.svg`,
          getThumbnailUrl: source => source?.thumbnailUrl ?? '',
          getThumbnailType: source => source?.entityType
        },
        {
          centered: true,
          columnClass: 'column-shrink',
          columnsAssigned: true,
          field: 'icon',
          isLink: false,
          isHidden: this.isMobile,
          isSortable: false,
          thumbnailType: this.baseThumbnailType,
          url: `https://static.digabit.com/${this.baseThumbnailType === 'page' ? 'page' : 'book'}.svg`,
          getThumbnailUrl: source => source?.thumbnailUrl ?? '',
          getThumbnailType: source => source?.entityType
        }
      ].concat(
        this.$store.state.user.hideIdentifier
          ? []
          : [{
              columnClass: 'column',
              field: this.isMedia
                ? 'productId'
                : 'identifier',
              fallbackField: 'productId',
              isLink: this.isWidget
                ? (this.widgetType === WidgetTypes.library)
                : true,
              isHidden: this.isMobile,
              isSortable: true,
              translation: this.isMedia
                ? this.$t('productId')
                : this.$t('identifier'),
              getToRoute: this.getToRouteUrl
            }]
      ).concat([
        {
          columnClass: 'column',
          field: 'name',
          translation: this.$t('name'),
          isHidden: this.isMobile,
          isLink: this.isWidget
            ? (this.widgetType === WidgetTypes.library)
            : true,
          isSortable: true,
          getToRoute: this.getToRouteUrl
        }
      ]).concat(this.isMedia
         ? []
         : [{
          columnClass: 'column',
          field: 'entityType',
          isHidden: this.isMobile,
          translation: this.$tc('type', 1),
          isLink: false,
          type: 'method',
          isSortable: true,
          conversionMethod: this.displayType
        }]
      )
    },
    footerTranslation () {
      const num = this.itemsTotal === 1 ? 1 : 2
      if (this.content.type === 'part')
        return this.$tc('result', num)
      else
        return this.$tc('media', num)
    },
    sortField () {
      let field = this.$store.getters['grids/activeColumn'](this.gridName) ?? 'identifier'
      if (this.isMobile) field = 'name'
      return field === 'identifier' ? this.identifierSortKey : field
    },
    sortInvert () {
      return this.$store.getters['grids/sortInvert'](this.gridName) ?? false
    },
    itemsTotal () {
      return this.$store.getters['grids/totalCount'](this.gridName)
    },
    excludeFilter () {
      if (this.content.type === 'part') {
        switch (this.partFilter.toUpperCase()) {
          case 'PAGE':
            return { entity_type: ['-Media', '-Productpage'] }
          case 'BOOK':
            return { entity_type: ['-Page', '-Productpage'] }
          case 'PRODUCTPAGE':
            return { entity_type: ['-Page', '-Media'] }
          default:
            return {}
        }
      }

      return {}
    },
    filter () {
      if (this.content.type === 'page') {
        return {
          entity_type: 'Media',
          fk_page_id: this.content.id,
          includeUnfilteredTotal: true
        }
      } else if (this.content.type === 'part') {
        return {
          fk_part_id: this.content.id,
          includeUnfilteredTotal: true,
          includeUnfilteredTotalIgnoreEntityType: true
        }
      } else if (this.content.type === 'media') {
        return {
          entity_type: 'Productpage',
          fk_media_id: this.content.id,
          includeUnfilteredTotal: true
        }
      } else {
        // should not happen
        return {
          entity_type: 'Media',
          includeUnfilteredTotal: true
        }
      }
    },
    sortClause () {
      if (!this.translatedSortField) return {}
      return {
        [this.sortInvert ? 'sort_desc' : 'sort_asc']: this.translatedSortField
      }
    },
    translatedSortField () {
      switch (this.sortField) {
        case 'name':
          return 'name_sort_' + this.$i18n.locale.replace('-', '_')
        case 'mediaType':
        case 'entityType':
          return 'entity_type'
        default:
          return this.sortField
      }
    },
    identifierSearchKey () {
      if (this.content.type === "media") {
        return "product_page_product_id"
      }
      return 'identifier'
    },
    identifierSortKey () {
      return 'identifier_sort'
    },
    showPageBookSelect () {
      if (this.content.type !== 'part') return false
      else return (!(this.hideBooksInSearch || this.hidePagesInSearch))
    },
    isMobile () {
      const mobile = this.screenWidth <= 1024
      return mobile
    },
    query () {
      switch (this.content.type) {
        case 'part':
          return '?toc="' + this.content.partNumber + '"'
        case 'page':
          return '?toc="' + this.content.originalName + '"'
        default:
          return ''
      }
    }
  },
  debounceMethods: {
    reset: 500,
    getMoreResults: 500
  },
  methods: {
    ...mapActions({
      addGridToStore: 'grids/addGrid',
      removeGrid: 'grids/removeGrid',
      setItemsTotal: 'grids/setTotalCount',
      setPagination: 'grids/setPagination',
      getContent: 'content/getContent',
      setGridSort: 'grids/setGridSort',
      setWhereUsedReceivedCount: 'content/setWhereUsedReceivedCount'
    }),
    async getMoreResults () {
      if (this.itemsTotal <= this.count) return
      await this.loadMore()
    },
    async loadMore () {
      if (!this.contentLoaded) return
      const searchId = ++this.searchInstance
      try {
        if (this.offsetNextCursor === 0) {
          this.items = []
        }
        const params = {
          ...this.filter,
          ...this.excludeFilter,
          ...this.searchFilters,
          ...this.sortClause,
          q: (this.searchParam && this.searchParam.length > 0) ? this.searchParam + "* OR " + this.searchParam : '',
          includeTagRollups: (this.searchParam && this.searchParam.length > 0) ? true : false,
          qf: ['name_', this.identifierSearchKey],
          limit: 100,
          offset: this.offsetNextCursor
        }
        const {
          data,
          headers
        } = await search(params)
        if (searchId !== this.searchInstance) return

        this.items.push(...data.items)
        const total = Number.parseInt(headers['x-total'])
        this.count = this.items.length
        await this.setPagination({
          grid: this.gridName,
          pagination: new Pagination(headers)
        })
        this.setItemsTotal({
          value: Number.parseInt(headers['x-count']),
          grid: this.gridName
        })
        this.offsetNextCursor = this.count
        this.setWhereUsedReceivedCount(total)
        this.$emit('whereUsedLoaded')
      } catch (error) {
        console.log('error :>> ', error)
      }
    },
    async reset () {
      this.isLoading = true
      this.offsetNextCursor = 0
      this.count = 0
      await this.loadMore()
      this.isLoading = false
    },
    displayType (field) {
      switch (field.toLowerCase()) {
        case "productpage":
          return this.$tc('productPage', 1)
        case "media":
          return this.$tc('book', 1)
        default:
          return this.$tc(field, 1)
      }
    },
    getToRouteUrl (source) {
      switch (source.entityType) {
        case "page":
          return `/page/${source.entityId}` + this.query
        case "productpage":
          return `/product-page/${source.entityId}`
            + `?partNumber="${this.content.partNumber}"`
        default:
          return `/book/${source.entityId}` + this.query
      }
    },
    handleResize () {
      this.screenWidth = window.innerWidth
    },
    updatePartFilter(value) {
      this.partFilter = value
    }
  },
  async created () {
    await this.addGridToStore({
      name: this.gridName,
      defaultColumn: this.isMobile ? 'name' : 'identifier'
    })
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
    this.reset()
  },
  beforeDestroy () {
    this.removeGrid(this.gridName)
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.grid-container {
  padding-left: 10px;
  padding-right: 10px;
  height: 100%;
}

.flex-grid {
  display: flex;
  flex-flow: column;
  flex: 1;
}

.padme-left-bottom {
  padding: 1.25rem 1rem;
  border-top: lightgray 1px solid;
}
.content-select {
  display: block;
  float: right;
}

.footer {
  min-width: 5em;
}
</style>
